import { FC, useMemo } from 'react';
import { TooltipProps } from 'recharts';
import { twMerge } from 'tailwind-merge';

import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import { ScenarioHooks, useSelectedCompany } from '../../../../../hooks/index';
import { Currency } from '../../../../../types/currency.types';
import { toMathRound } from '../../../../../utils/to-math-round.util';
import { EmptyScenarioCapTable } from '../../../scenario-details/empty-cap-table';
import { LiquidityGraphDesktop } from './liquidity-graph-desktop';
import { LiquidityMobileGraph } from './liquidity-mobile-graph';

export interface GraphDataItem {
  investment: number;
  label: string;
  liquidity: number;
  roi: number;
}

interface CustomTickProps {
  currentStakeholderId: string;
  payload: { value: string };
  x: number;
  y: number;
}

const truncateText = (text: string, maxWidth: number, font = '500 10px sans-serif') => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return text;

  ctx.font = font;

  if (ctx.measureText(text).width <= maxWidth) return text;

  let truncated = text;
  while (truncated.length > 0 && ctx.measureText(truncated + '…').width > maxWidth) {
    truncated = truncated.slice(0, -1);
  }
  return truncated + '…';
};

export const CustomXAxisTick: FC<CustomTickProps> = ({
  x,
  y,
  payload,
  currentStakeholderId,
}: CustomTickProps) => {
  const [name, stakeholderId] = payload.value.split('|');
  const trimmed = truncateText(name, 80);
  const isCurrent = stakeholderId === currentStakeholderId;

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        dy={isCurrent ? 10 : 16}
        fill="#475467"
        fontSize={10}
        fontWeight={500}
        style={{ lineHeight: '16px', dominantBaseline: 'middle' }}
        textAnchor="middle"
        x={0}
        y={0}
      >
        {trimmed}
      </text>
      {isCurrent && (
        <text
          dy={22}
          fill="#475467"
          fontSize={10}
          fontWeight={400}
          style={{ lineHeight: '16px', dominantBaseline: 'middle' }}
          textAnchor="middle"
          x={0}
          y={0}
        >
          <AppFormattedMessage id="YOU" />
        </text>
      )}
    </g>
  );
};

export const CustomYAxisTick: FC<CustomTickProps> = ({
  x,
  y,
  payload,
  currentStakeholderId,
}: CustomTickProps) => {
  const [name, stakeholderId] = payload.value.split('|');
  const trimmed = truncateText(name, 80);
  const isCurrent = stakeholderId === currentStakeholderId;

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        dy={0}
        fill="#475467"
        fontSize={10}
        fontWeight={500}
        style={{ lineHeight: '16px', dominantBaseline: 'middle' }}
        textAnchor="middle"
        x={-40}
        y={0}
      >
        {trimmed}
      </text>
      {isCurrent && (
        <text
          dy={12}
          fill="#475467"
          fontSize={10}
          fontWeight={400}
          style={{ lineHeight: '16px', dominantBaseline: 'middle' }}
          textAnchor="middle"
          x={-40}
          y={0}
        >
          (<AppFormattedMessage id="YOU" />)
        </text>
      )}
    </g>
  );
};

interface CustomToolTipProps extends TooltipProps<number, string> {
  currency?: Currency | undefined;
}

export const CustomToolTip: FC<CustomToolTipProps> = ({ active, payload, currency }) => {
  if (active && payload && payload.length) {
    const { investment, liquidity, roi } = payload[0].payload;
    return (
      <div className="flex w-[224px] flex-col gap-2 rounded-lg bg-gray-25 px-1 pt-2 shadow-2xl">
        <div className="flex justify-between gap-1 border-b-[1px] border-gray-200 px-4 pb-2">
          <span className="text-label-sm font-semibold text-gray-700">
            <AppFormattedMessage id="INVESTMENT" />
          </span>
          <span className="text-label-sm font-semibold text-forest-600">
            {currency?.symbol || currency?.iso3?.toUpperCase()}
            {investment.toLocaleString('en-US')}
          </span>
        </div>
        <div className="flex justify-between gap-1 border-b-[1px] border-dashed border-gray-200 px-4 pb-2">
          <span className="text-label-sm font-semibold text-gray-700">
            {<AppFormattedMessage id="LIQUIDITY" />}
          </span>
          <span className="text-label-sm font-semibold text-gray-700">
            {currency?.symbol || currency?.iso3?.toUpperCase()}
            {liquidity.toLocaleString('en-US')}
          </span>
        </div>
        <div className="flex justify-between gap-1 px-4 pb-2">
          <span className="text-label-sm font-semibold text-gray-700">
            <AppFormattedMessage id="ROI" />
          </span>
          <span className="text-label-sm font-semibold text-gray-700">{toMathRound(roi)} x</span>
        </div>
      </div>
    );
  }
};

export const LiquidityGraphWrapper: FC<{ isMobile?: boolean }> = ({ isMobile = false }) => {
  const { selectedCompany } = useSelectedCompany();
  const currency = selectedCompany?.currency;
  const currentStakeholderId = selectedCompany?.stakeholder?.id || '';
  const currentStakeholderName = selectedCompany?.stakeholder?.fullName;
  const currentStakeholderLabel = `${currentStakeholderName}|${currentStakeholderId}`;

  const { data } = ScenarioHooks.useLiquidityGraphData();

  const graphData = data.map(
    ({ effectiveLiquidityProceeds, investment, roi, stakeholder, name }) => ({
      label: `${name}|${stakeholder.id}`,
      investment,
      roi,
      liquidity: effectiveLiquidityProceeds,
    }),
  );

  const transformGraphData = (
    graphData: GraphDataItem[],
    currentLabel: string,
  ): GraphDataItem[] => {
    if (!graphData?.length) return [];

    const currentIndex = graphData.findIndex((item) => item.label === currentLabel);
    const currentItem = currentIndex >= 0 ? graphData[currentIndex] : null;
    const withoutCurrentItem = currentItem
      ? graphData.filter((_, i) => i !== currentIndex)
      : graphData;

    const sliceIndex = currentItem ? 8 : 9;
    const top = withoutCurrentItem.slice(0, sliceIndex);
    const rest = withoutCurrentItem.slice(sliceIndex);

    const others: GraphDataItem | null = rest.length
      ? {
          label: 'Others',
          investment: rest.reduce((sum, item) => sum + item.investment, 0),
          roi: rest.reduce((sum, item) => sum + item.roi, 0),
          liquidity: rest.reduce((sum, item) => sum + item.liquidity, 0),
        }
      : null;

    const result = [...(currentItem ? [currentItem] : []), ...top, ...(others ? [others] : [])];

    return result;
  };

  const transformedData = useMemo(() => {
    return transformGraphData(graphData, currentStakeholderLabel);
  }, [graphData, currentStakeholderLabel]);

  return (
    <>
      {transformedData?.length > 0 ? (
        <div
          className={twMerge(
            isMobile
              ? 'flex h-full min-h-[512px] w-full rounded-none p-0 shadow-none'
              : 'min-h-[510px] rounded-md px-8 py-[54px] shadow-sm',
          )}
        >
          <div className={twMerge('h-full w-full', isMobile && 'mr-6')}>
            {isMobile ? (
              <LiquidityMobileGraph data={transformedData} stakeholderId={currentStakeholderId} />
            ) : (
              <LiquidityGraphDesktop
                currency={currency}
                data={transformedData}
                stakeholderId={currentStakeholderId}
              />
            )}
          </div>
        </div>
      ) : (
        <EmptyScenarioCapTable />
      )}
    </>
  );
};
